import { useState } from 'react';
import { Phone, Mail, Globe, MapPin, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND, INTEREST_OPTIONS } from '@/lib/brand';
import { submitConsultation } from '@/lib/api';

export default function Consultation() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', interest: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please share your name and email.');
      return;
    }
    setLoading(true);
    try {
      await submitConsultation(form);
      setDone(true);
      toast.success('Request received. Anthony will follow up personally.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="consultation-section"
      className="relative bg-[#070B12] py-24 md:py-32 overflow-hidden"
    >
      {/* Ambient glow */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(182,138,53,0.35) 0%, transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: intro */}
          <div className="lg:col-span-5">
            <div className="overline text-[#D6B36A] mb-6">Consultation</div>
            <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
              Begin With a<br />
              <span className="italic gold-text">Conversation</span>.
            </h2>
            <p className="text-white/70 text-base md:text-lg leading-relaxed font-light mb-8">
              Every strategy begins with a conversation — not a pitch. Share a bit about your goals and
              Anthony will personally follow up to discuss your next steps.
            </p>

            {/* Contact card */}
            <div className="bg-[#0E2238] border border-[#B68A35]/30 p-8 md:p-10 space-y-5">
              <p className="text-[#D6B36A] text-[0.7rem] tracking-[0.28em] uppercase font-medium mb-2">Direct Contact</p>

              <a href={BRAND.phoneHref} data-testid="contact-card-phone" className="flex items-center gap-4 text-white/85 hover:text-[#D6B36A] transition-colors group">
                <div className="w-10 h-10 border border-[#B68A35]/40 flex items-center justify-center group-hover:border-[#D6B36A] transition-colors">
                  <Phone className="w-4 h-4 text-[#D6B36A]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/40 tracking-wider uppercase">Phone</p>
                  <p className="text-base tracking-wide">{BRAND.phone}</p>
                </div>
              </a>

              <a href={BRAND.emailHref} data-testid="contact-card-email" className="flex items-center gap-4 text-white/85 hover:text-[#D6B36A] transition-colors group">
                <div className="w-10 h-10 border border-[#B68A35]/40 flex items-center justify-center group-hover:border-[#D6B36A] transition-colors">
                  <Mail className="w-4 h-4 text-[#D6B36A]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/40 tracking-wider uppercase">Email</p>
                  <p className="text-base">{BRAND.email}</p>
                </div>
              </a>

              <a href={BRAND.websiteHref} target="_blank" rel="noreferrer" data-testid="contact-card-website" className="flex items-center gap-4 text-white/85 hover:text-[#D6B36A] transition-colors group">
                <div className="w-10 h-10 border border-[#B68A35]/40 flex items-center justify-center group-hover:border-[#D6B36A] transition-colors">
                  <Globe className="w-4 h-4 text-[#D6B36A]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/40 tracking-wider uppercase">Web</p>
                  <p className="text-base">{BRAND.website}</p>
                </div>
              </a>

              <div className="pt-4 border-t border-white/10 flex items-start gap-3 text-white/60 text-xs font-light">
                <MapPin className="w-4 h-4 mt-0.5 text-[#B68A35]/70 shrink-0" strokeWidth={1.5} />
                <p>Serving families, educators, business owners, and faith-based communities across the United States.</p>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="bg-white p-8 md:p-12 border border-[#B68A35]/20 relative">
              <div className="absolute top-0 left-0 w-16 h-16 border-l border-t border-[#B68A35]" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-r border-b border-[#B68A35]" />

              {done ? (
                <div data-testid="consultation-success" className="py-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#B68A35]/15 flex items-center justify-center mx-auto mb-6 check-in">
                    <Check className="w-8 h-8 text-[#B68A35]" strokeWidth={2} />
                  </div>
                  <h3 className="font-serif text-[#0E2238] text-3xl md:text-4xl mb-4 fade-up" style={{ animationDelay: '200ms' }}>Thank You.</h3>
                  <p className="text-[#2B2B2B]/70 text-base font-light max-w-md mx-auto fade-up" style={{ animationDelay: '350ms' }}>
                    Your request has been received. Anthony will personally follow up within one business day
                    to discuss your goals and next steps.
                  </p>
                  <div className="mx-auto mt-6 w-16 h-px bg-[#B68A35]/40 fade-in" style={{ animationDelay: '500ms' }} />
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="consultation-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <div>
                      <label className="text-[#0E2238]/50 text-[0.7rem] tracking-[0.24em] uppercase">Full Name</label>
                      <input value={form.name} onChange={update('name')} type="text" placeholder="Your name" className="lux-input" data-testid="consultation-name" />
                    </div>
                    <div>
                      <label className="text-[#0E2238]/50 text-[0.7rem] tracking-[0.24em] uppercase">Email</label>
                      <input value={form.email} onChange={update('email')} type="email" placeholder="you@example.com" className="lux-input" data-testid="consultation-email" />
                    </div>
                    <div>
                      <label className="text-[#0E2238]/50 text-[0.7rem] tracking-[0.24em] uppercase">Phone</label>
                      <input value={form.phone} onChange={update('phone')} type="tel" placeholder="(555) 555-5555" className="lux-input" data-testid="consultation-phone" />
                    </div>
                    <div>
                      <label className="text-[#0E2238]/50 text-[0.7rem] tracking-[0.24em] uppercase">I Am Interested In</label>
                      <select
                        value={form.interest}
                        onChange={update('interest')}
                        className="lux-input bg-transparent"
                        data-testid="consultation-interest"
                      >
                        <option value="">Select an area</option>
                        {INTEREST_OPTIONS.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="text-[#0E2238]/50 text-[0.7rem] tracking-[0.24em] uppercase">Message</label>
                    <textarea value={form.message} onChange={update('message')} rows="4" placeholder="Share a bit about your goals or questions…" className="lux-input" data-testid="consultation-message" />
                  </div>

                  <p className="mt-6 text-[#2B2B2B]/60 text-xs font-light italic">
                    Anthony will personally follow up to discuss your goals and next steps.
                  </p>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="consultation-submit"
                    className="btn-gold mt-8 w-full md:w-auto disabled:opacity-60"
                  >
                    {loading ? 'Sending…' : 'Request a Complimentary Consultation'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
